/* eslint-disable fp/no-mutation */
import { Env } from './env';
import { injectCustomerIoScript, injectGoogleTagManagerScript, isValidMarketingId } from './marketingScriptLoaders';

const MARKETING_SCRIPT_IDLE_TIMEOUT_MS = 3000;
const MARKETING_SCRIPT_FALLBACK_DELAY_MS = 1500;

type MarketingScriptTask = {
  load: () => boolean;
  name: string;
};

type MarketingScriptTaskConfig = MarketingScriptTask & {
  shouldLoad: boolean;
};

const scheduleMarketingScriptLoad = (browserWindow: Window, load: () => boolean): void => {
  if ('requestIdleCallback' in browserWindow && typeof browserWindow.requestIdleCallback === 'function') {
    browserWindow.requestIdleCallback(load, { timeout: MARKETING_SCRIPT_IDLE_TIMEOUT_MS });
    return;
  }

  browserWindow.setTimeout(load, MARKETING_SCRIPT_FALLBACK_DELAY_MS);
};

const getMarketingScriptTasks = (browserWindow: Window, documentRef: Document): MarketingScriptTask[] => {
  const taskConfigs: MarketingScriptTaskConfig[] = [
    {
      load: () =>
        injectGoogleTagManagerScript({
          browserWindow,
          documentRef,
          gtmId: Env.VITE_GTM_ID,
        }),
      name: 'GTM',
      shouldLoad: isValidMarketingId(Env.VITE_GTM_ID),
    },
    {
      load: () =>
        injectCustomerIoScript({
          browserWindow,
          documentRef,
          siteId: Env.VITE_CUSTOMER_IO_SITE_ID,
        }),
      name: 'Customer.io',
      shouldLoad: isValidMarketingId(Env.VITE_CUSTOMER_IO_SITE_ID),
    },
  ];

  return taskConfigs.filter((task) => task.shouldLoad).map(({ load, name }) => ({ load, name }));
};

export const releaseMarketingScripts = (browserWindow: Window = window, documentRef: Document = document): void => {
  const tasks = getMarketingScriptTasks(browserWindow, documentRef);

  browserWindow.duckbillMarketingHtmlExpected = tasks.length;
  browserWindow.duckbillMarketingHtmlCompleted = 0;
  browserWindow.duckbillRecordMarketingDebugEvent?.('marketing_scripts_scheduled', {
    scripts: tasks.map((task) => task.name).join(','),
  });
  browserWindow.duckbillTryMarketingLoadEnd?.();

  tasks.forEach(({ load }) => {
    scheduleMarketingScriptLoad(browserWindow, load);
  });
};
